import { useCallback, useRef, useState } from 'react';
import { CONFIRM_AS_MODAL, sendConfirmStream, sendGrpcStream } from '../services/brunixStream';
import SessionService from '../services/SessionsService';
import type { ConfirmationPayload, Message, MessagePart } from '../components/AiBrunixPanel/BrunixMessageList/brunix';

export interface UseBrunixStreamOptions {
  sessionId: string;
  initialMessages?: Message[];
  contexts?: any[];
  mcpServers?: any[];
  orgId?: string;
  projectId?: string;
}

export function useBrunixStream({
  sessionId,
  initialMessages = [],
  contexts = [],
  mcpServers = [],
  orgId,
  projectId
}: UseBrunixStreamOptions) {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMsgId, setStreamingMsgId] = useState<string | null>(null);
  const [llmCalling, setLlmCalling] = useState<string | null>(null);
  const [streamJustFinished, setStreamJustFinished] = useState<string | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = useState<{ runId: string; payload: ConfirmationPayload } | null>(null);
  const [slotInputs, setSlotInputs] = useState<Record<string, Record<string, Record<string, string>>>>({});
  const [stepSelections, setStepSelections] = useState<Record<string, boolean[]>>({});
  const [retryArgsText, setRetryArgsText] = useState<Record<string, string>>({});
  const [isTyping, setIsTyping] = useState(false);
  const [lastStepReached, setLastStepReached] = useState(false);

  const grpcAbortRef = useRef<AbortController | null>(null);
  const abortHandlerRef = useRef<(() => void) | null>(null);

  const sendMessage = useCallback((userContent: string, assistantMsgId?: string) => {
    const userMsgId = Date.now().toString();
    const resolvedAssistantMsgId = assistantMsgId || (Date.now() + 1).toString();
    const parts: MessagePart[] = [];
    let msgCreated = false;
    const startedAt = Date.now();

    const ctrl = new AbortController();
    grpcAbortRef.current = ctrl;
    setIsStreaming(true);
    setStreamingMsgId(resolvedAssistantMsgId);

    // Show analyze_followup immediately so the chat isn't blank while prepare_graph runs
    parts.push({ type: 'step', content: 'analyze_followup', message: 'checking context...' });
    msgCreated = true;
    setIsTyping(false);
    setMessages(prev => [
      ...prev,
      { id: userMsgId, role: 'user', content: userContent, timestamp: new Date() },
      {
        id: resolvedAssistantMsgId, role: 'assistant',
        content: '', parts: parts.map(p => ({ ...p })), timestamp: new Date(), startedAt,
      }
    ]);

    const syncNow = () => {
      const snapshot = parts.map(p => ({ ...p }));
      if (!msgCreated) {
        msgCreated = true;
        setIsTyping(false);
        setMessages(prev => [...prev, {
          id: resolvedAssistantMsgId, role: 'assistant',
          content: '', parts: snapshot, timestamp: new Date(), startedAt,
        }]);
      } else {
        setMessages(prev => prev.map(m =>
          m.id === resolvedAssistantMsgId ? { ...m, parts: snapshot } : m
        ));
      }
    };

    let _tokenQueue: string[] = [];
    let _rafId: number | null = null;
    let _streamDone = false;
    let _deferredFinish: (() => void) | null = null;
    let _finishCalled = false;
    let _firstTokenRendered = false;

    const _renderOneToken = () => {
      _rafId = null;
      if (_tokenQueue.length === 0) {
        if (_streamDone && _deferredFinish) {
          const fn = _deferredFinish;
          _deferredFinish = null;
          requestAnimationFrame(() => setTimeout(fn, 0));
        }
        return;
      }
      const token = _tokenQueue.shift()!;
      const last = parts[parts.length - 1];
      if (last?.type === 'text') last.content += token;
      else parts.push({ type: 'text', content: token });

      if (!_firstTokenRendered) {
        _firstTokenRendered = true;
      }

      syncNow();
      if (_tokenQueue.length > 0) {
        _rafId = requestAnimationFrame(_renderOneToken);
      } else if (_streamDone && _deferredFinish) {
        // Run once more with an empty queue so the deferred completion clears
        // the streaming state after React has painted the final token.
        _rafId = requestAnimationFrame(_renderOneToken);
      }
    };

    const enqueueToken = (text: string) => {
      _tokenQueue.push(text);
      if (_rafId === null) {
        _rafId = requestAnimationFrame(_renderOneToken);
      }
    };

    const finish = () => {
      abortHandlerRef.current = null;
      setIsTyping(false);
      setIsStreaming(false);
      setStreamingMsgId(null);
      setLlmCalling(null);
      setStreamJustFinished(resolvedAssistantMsgId);
      setMessages(prev => prev.map(m =>
        m.id === resolvedAssistantMsgId ? { ...m, elapsedMs: Date.now() - startedAt } : m
      ));
      setTimeout(() => {
        setStreamJustFinished(null);
      }, 500);
      grpcAbortRef.current = null;
    };

    const actualFinish = finish;

    const wrappedFinish = () => {
      if (_finishCalled) return;
      _finishCalled = true;
      _streamDone = true;

      if (_tokenQueue.length > 0 || _rafId !== null) {
        _deferredFinish = actualFinish;
        if (_tokenQueue.length > 0 && _rafId === null) {
          _rafId = requestAnimationFrame(_renderOneToken);
        }
      } else {
        requestAnimationFrame(() => setTimeout(actualFinish, 0));
      }
    };

    const toB64 = (s: string) => btoa(unescape(encodeURIComponent(s)));
    const activeContexts = contexts.filter(c => c.isActive && c.value);
    const activeContextText = activeContexts
      .map(c => `### ${c.name}\n${c.value}`)
      .join('\n\n');
    const extraContext = activeContextText ? toB64(activeContextText) : undefined;

    const activeMcpServers = mcpServers
      .filter(m => m.isActive && m.url)
      .map(m => m.internal
        ? { type: 'internal', name: m.name, url: m.url }
        : { type: 'external', name: m.name, url: m.url, bearer_token: m.bearer }
      );

    abortHandlerRef.current = () => {
      parts.push({ type: 'cancelled', content: '' });
      syncNow();
      wrappedFinish();
    };

    sendGrpcStream(
      userContent, sessionId,
      (step, message) => {
        const synthIdx = parts.findIndex(p => p.type === 'step' && p.content === 'analyze_followup' && p.message === 'checking context...');
        if (synthIdx !== -1 && step === 'analyze_followup') {
          parts[synthIdx] = { type: 'step', content: step, message };
        } else if (synthIdx !== -1 && step !== 'analyze_followup') {
          parts[synthIdx] = { type: 'step', content: 'analyze_followup', message };
          parts.push({ type: 'step', content: step, message });
        } else {
          parts.push({ type: 'step', content: step, message });
        }
        syncNow();
      },
      (text) => {
        if (!text) return;
        enqueueToken(text);
      },
      wrappedFinish,
      (error) => {
        finish();
        if (!msgCreated) {
          setMessages(prev => [...prev, {
            id: resolvedAssistantMsgId, role: 'assistant',
            content: `⚠️ ${error}`, timestamp: new Date(),
          }]);
        }
      },
      ctrl.signal,
      (runId, payload) => {
        const initSlots: Record<string, Record<string, string>> = {};
        (payload.param_resolution?.resolved ?? []).forEach(p => {
          if (p.status === 'runtime') return;
          const k = String(p.step);
          if (!initSlots[k]) initSlots[k] = {};
          if (p.value !== null && p.value !== undefined) initSlots[k][p.param] = String(p.value);
        });
        setSlotInputs(prev => ({ ...prev, [runId]: initSlots }));
        setStepSelections(prev => ({ ...prev, [runId]: (payload.plan ?? []).map(() => true) }));
        parts.push({ type: 'confirmation', content: payload.message, runId, confirmationPayload: payload, confirmed: null });
        syncNow();
        if (CONFIRM_AS_MODAL) setPendingConfirmation({ runId, payload });
      },
      (plan, message, summary, paramResolution, missing) => {
        parts.push({ type: 'inform', content: message, plan, summary, paramResolution, missing });
        syncNow();
      },
      (progress) => {
        if (progress.tool === 'compile_avap' && progress.status === 'ok' && progress.output?.bytecode) {
          const idx = parts.reduce((last, p, i) => (p.type === 'step' && p.content === 'compile_avap' ? i : last), -1);
          if (idx >= 0) parts[idx] = { ...parts[idx], stepData: progress.output.bytecode };
        } else if (progress.tool === 'validate_avap' && progress.output !== undefined) {
          const idx = parts.reduce((last, p, i) => (p.type === 'step' && p.content === 'validate_avap' ? i : last), -1);
          if (idx >= 0) parts[idx] = { ...parts[idx], stepData: progress.output };
        } else if (progress.tool === 'run_avap' && progress.output !== undefined) {
          const idx = parts.reduce((last, p, i) => (p.type === 'step' && p.content === 'run_avap' ? i : last), -1);
          if (idx >= 0) parts[idx] = { ...parts[idx], stepData: progress.output };
        } else if (progress.tool === 'check_compile_context' && progress.output !== undefined) {
          const idx = parts.reduce((last, p, i) => (p.type === 'step' && p.content === 'check_compile_context' ? i : last), -1);
          if (idx >= 0) parts[idx] = { ...parts[idx], stepData: progress.output };
        } else if (progress.tool === 'check_avap_completeness' && progress.output !== undefined) {
          const idx = parts.reduce((last, p, i) => (p.type === 'step' && p.content === 'check_avap_completeness' ? i : last), -1);
          if (idx >= 0) parts[idx] = { ...parts[idx], stepData: progress.output };
        } else if (progress.tool === 'analyze_followup' && progress.output !== undefined) {
          const idx = parts.reduce((last, p, i) => (p.type === 'step' && p.content === 'analyze_followup' ? i : last), -1);
          if (idx >= 0) parts[idx] = { ...parts[idx], stepData: progress.output };
        } else if (progress.tool === 'syntax_validation' && progress.output !== undefined) {
          const idx = parts.reduce((last, p, i) => (p.type === 'step' && p.content === 'syntax_validation' ? i : last), -1);
          if (idx >= 0) parts[idx] = { ...parts[idx], stepData: progress.output };
        } else if (progress.tool === 'solving_issues' && progress.output !== undefined) {
          const idx = parts.reduce((last, p, i) => (p.type === 'step' && typeof p.content === 'string' && p.content.startsWith('solving_issues') ? i : last), -1);
          if (idx >= 0) parts[idx] = { ...parts[idx], stepData: progress.output };
        } else {
          const idx = parts.findIndex(p => p.type === 'step_progress' && p.stepProgress?.step === progress.step && p.stepProgress?.tool === progress.tool);
          if (idx >= 0) parts[idx] = { type: 'step_progress', content: '', stepProgress: progress };
          else parts.push({ type: 'step_progress', content: '', stepProgress: progress });
        }
        syncNow();
      },
      extraContext,
      activeMcpServers.length > 0 ? activeMcpServers : undefined,
      orgId ?? SessionService.ORGANIZATION,
      projectId ?? SessionService.SEL_PROJECT,
      (tasks) => {
        parts.push({ type: 'task_graph', content: '', taskGraph: tasks });
        syncNow();
      },
      (intent, method) => {
        parts.push({ type: 'classify_result', content: '', classifyIntent: intent, classifyMethod: method });
        syncNow();
      },
      (processId, steps) => {
        parts.push({ type: 'plan_ready', content: '', processId, planSteps: steps });
        syncNow();
      },
      (status, node) => {
        setLlmCalling(status === 'start' ? node : null);
      },
      (count) => {
        parts.push({ type: 'tools_loaded', content: '', toolsCount: count });
        syncNow();
      },
      (processId) => {
        parts.push({ type: 'playbook_found', content: '', processId });
        syncNow();
      },
      (processId, tools) => {
        parts.push({ type: 'template_found', content: '', processId, templateTools: tools });
        syncNow();
      },
      () => abortHandlerRef.current?.(),
    );
  }, [sessionId, contexts, mcpServers, orgId, projectId]);

  const confirmAction = useCallback((runId: string, confirmed: boolean) => {
    setMessages(prev => prev.map(m => ({
      ...m,
      parts: m.parts?.map(p => p.type === 'confirmation' && p.runId === runId ? { ...p, confirmed } : p),
    })));
    setPendingConfirmation(null);

    const filledSlots = slotInputs[runId] ?? {};
    setSlotInputs(prev => { const n = { ...prev }; delete n[runId]; return n; });

    const selections = stepSelections[runId] ?? [];
    const selectedSteps = selections.length > 0 ? selections.map((sel, i) => sel ? i : -1).filter(i => i >= 0) : undefined;
    setStepSelections(prev => { const n = { ...prev }; delete n[runId]; return n; });

    const execMsgId = Date.now().toString() + '-exec';
    const execParts: MessagePart[] = [];
    let msgCreated = false;
    const startedAt = Date.now();

    const syncExec = () => {
      const snapshot = execParts.map(p => ({ ...p }));
      if (!msgCreated) {
        msgCreated = true;
        setMessages(prev => [...prev, { id: execMsgId, role: 'assistant', content: '', parts: snapshot, timestamp: new Date(), startedAt }]);
      } else {
        setMessages(prev => prev.map(m => m.id === execMsgId ? { ...m, parts: snapshot } : m));
      }
    };

    setIsStreaming(true);
    setStreamingMsgId(execMsgId);
    sendConfirmStream(
      runId, confirmed, sessionId,
      (step, message) => { execParts.push({ type: 'step', content: step, message }); syncExec(); },
      (text) => {
        const last = execParts[execParts.length - 1];
        if (last?.type === 'text') last.content += text;
        else execParts.push({ type: 'text', content: text });
        syncExec();
      },
      () => {
        setIsStreaming(false);
        setStreamingMsgId(null);
        setStreamJustFinished(execMsgId);
        setMessages(prev => prev.map(m => m.id === execMsgId ? { ...m, elapsedMs: Date.now() - startedAt } : m));
        setTimeout(() => setStreamJustFinished(null), 500);
      },
      (error) => { execParts.push({ type: 'text', content: `⚠️ ${error}` }); syncExec(); setIsStreaming(false); setStreamingMsgId(null); },
      (toolResults) => { execParts.push({ type: 'tool_result', content: '', toolResults }); syncExec(); },
      (progress) => {
        const _spIdx = execParts.findIndex(p => p.type === 'step_progress' && p.stepProgress?.step === progress.step && p.stepProgress?.tool === progress.tool);
        if (_spIdx >= 0) {
          execParts[_spIdx] = { type: 'step_progress', content: '', stepProgress: progress, pipelineSteps: execParts[_spIdx].pipelineSteps };
        } else {
          execParts.push({ type: 'step_progress', content: '', stepProgress: progress });
        }
        syncExec();
      },
      undefined,
      Object.keys(filledSlots).length > 0 ? filledSlots : undefined,
      selectedSteps,
      (nestedRunId, payload) => {
        const initSlots: Record<string, Record<string, string>> = {};
        (payload.param_resolution?.resolved ?? []).forEach(p => {
          if (p.status === 'runtime') return;
          const k = String(p.step);
          if (!initSlots[k]) initSlots[k] = {};
          if (p.value !== null && p.value !== undefined) initSlots[k][p.param] = String(p.value);
        });
        setSlotInputs(prev => ({ ...prev, [nestedRunId]: initSlots }));
        setStepSelections(prev => ({ ...prev, [nestedRunId]: (payload.plan ?? []).map(() => true) }));
        execParts.push({ type: 'confirmation', content: payload.message ?? '', runId: nestedRunId, confirmationPayload: payload, confirmed: null });
        syncExec();
        setIsStreaming(false);
        setStreamingMsgId(null);
      },
      (status, node) => {
        if (status === 'start' && node === 'slot_resolver') {
          execParts.push({ type: 'step', content: 'Slot Resolver', message: 'preparing parameters...' });
          setLlmCalling('slot_resolver');
          syncExec();
        } else if (status === 'start' && node === 'responder') {
          setLlmCalling(null);
          execParts.push({ type: 'generating_response', content: '' });
          syncExec();
        } else if (status === 'start') {
          setLlmCalling(node);
        } else if (status === 'done') {
          setLlmCalling(null);
          const idx = execParts.reduce((last, p, i) => p.type === 'generating_response' ? i : last, -1);
          if (idx >= 0) execParts.splice(idx, 1);
          syncExec();
        }
      },
      undefined,
      undefined,
      (pipelineStep) => {
        const _attachTarget = execParts.reduce((last, p, i) => p.type === 'step_progress' ? i : last, -1) >= 0
          ? execParts.reduce((last, p, i) => p.type === 'step_progress' ? i : last, -1)
          : execParts.length - 1;
        if (_attachTarget >= 0) {
          const prev = execParts[_attachTarget].pipelineSteps ?? [];
          const existingIdx = prev.findIndex(ps => ps.node === pipelineStep.node);
          const updated = existingIdx >= 0 ? prev.map((ps, i) => i === existingIdx ? { ...ps, ...pipelineStep } : ps) : [...prev, pipelineStep];
          execParts[_attachTarget] = { ...execParts[_attachTarget], pipelineSteps: updated };
        }
        syncExec();
      },
    );
  }, [sessionId, slotInputs, stepSelections]);

  const retryAction = useCallback((runId: string, retryArgs: any | undefined) => {
    setMessages(prev => prev.map(m => ({
      ...m,
      parts: m.parts?.map(p => p.type === 'confirmation' && p.runId === runId ? { ...p, confirmed: true } : p),
    })));
    setRetryArgsText(prev => { const n = { ...prev }; delete n[runId]; return n; });

    const execMsgId = Date.now().toString() + '-retry';
    const execParts: MessagePart[] = [];
    let msgCreated = false;
    const startedAt = Date.now();

    const syncExec = () => {
      const snapshot = execParts.map(p => ({ ...p }));
      if (!msgCreated) {
        msgCreated = true;
        setMessages(prev => [...prev, { id: execMsgId, role: 'assistant', content: '', parts: snapshot, timestamp: new Date(), startedAt }]);
      } else {
        setMessages(prev => prev.map(m => m.id === execMsgId ? { ...m, parts: snapshot } : m));
      }
    };

    setIsStreaming(true);
    setStreamingMsgId(execMsgId);
    sendConfirmStream(
      runId, true, sessionId,
      (step, message) => { execParts.push({ type: 'step', content: step, message }); syncExec(); },
      (text) => {
        const last = execParts[execParts.length - 1];
        if (last?.type === 'text') last.content += text;
        else execParts.push({ type: 'text', content: text });
        syncExec();
      },
      () => {
        setIsStreaming(false);
        setStreamingMsgId(null);
        setStreamJustFinished(execMsgId);
        setMessages(prev => prev.map(m => m.id === execMsgId ? { ...m, elapsedMs: Date.now() - startedAt } : m));
        setTimeout(() => setStreamJustFinished(null), 500);
      },
      (error) => { execParts.push({ type: 'text', content: `⚠️ ${error}` }); syncExec(); setIsStreaming(false); setStreamingMsgId(null); },
      (toolResults) => { execParts.push({ type: 'tool_result', content: '', toolResults }); syncExec(); },
      (progress) => { execParts.push({ type: 'step_progress', content: '', stepProgress: progress }); syncExec(); },
      undefined,
      undefined,
      undefined,
      (nestedRunId, payload) => {
        execParts.push({ type: 'confirmation', content: payload.message ?? '', runId: nestedRunId, confirmationPayload: payload, confirmed: null });
        syncExec();
        setIsStreaming(false);
        setStreamingMsgId(null);
      },
      undefined,
      retryArgs,
    );
  }, [sessionId]);

  const skipAction = useCallback((runId: string) => {
    setMessages(prev => prev.map(m => ({
      ...m,
      parts: m.parts?.map(p => p.type === 'confirmation' && p.runId === runId ? { ...p, confirmed: true } : p),
    })));
    setRetryArgsText(prev => { const n = { ...prev }; delete n[runId]; return n; });

    const execMsgId = Date.now().toString() + '-skip';
    const execParts: MessagePart[] = [];
    let msgCreated = false;
    const startedAt = Date.now();

    const syncExec = () => {
      const snapshot = execParts.map(p => ({ ...p }));
      if (!msgCreated) {
        msgCreated = true;
        setMessages(prev => [...prev, { id: execMsgId, role: 'assistant', content: '', parts: snapshot, timestamp: new Date(), startedAt }]);
      } else {
        setMessages(prev => prev.map(m => m.id === execMsgId ? { ...m, parts: snapshot } : m));
      }
    };

    setIsStreaming(true);
    setStreamingMsgId(execMsgId);
    sendConfirmStream(
      runId, true, sessionId,
      (step, message) => { execParts.push({ type: 'step', content: step, message }); syncExec(); },
      (text) => {
        const last = execParts[execParts.length - 1];
        if (last?.type === 'text') last.content += text;
        else execParts.push({ type: 'text', content: text });
        syncExec();
      },
      () => {
        setIsStreaming(false);
        setStreamingMsgId(null);
        setStreamJustFinished(execMsgId);
        setMessages(prev => prev.map(m => m.id === execMsgId ? { ...m, elapsedMs: Date.now() - startedAt } : m));
        setTimeout(() => setStreamJustFinished(null), 500);
      },
      (error) => { execParts.push({ type: 'text', content: `⚠️ ${error}` }); syncExec(); setIsStreaming(false); setStreamingMsgId(null); },
      (toolResults) => { execParts.push({ type: 'tool_result', content: '', toolResults }); syncExec(); },
      (progress) => { execParts.push({ type: 'step_progress', content: '', stepProgress: progress }); syncExec(); },
      undefined,
      undefined,
      undefined,
      (nestedRunId, payload) => {
        execParts.push({ type: 'confirmation', content: payload.message ?? '', runId: nestedRunId, confirmationPayload: payload, confirmed: null });
        syncExec();
        setIsStreaming(false);
        setStreamingMsgId(null);
      },
      undefined,
      undefined,
      true
    );
  }, [sessionId]);

  const cancelStream = useCallback(() => {
    if (grpcAbortRef.current) {
      grpcAbortRef.current.abort();
    }
  }, []);

  const reset = useCallback(() => {
    cancelStream();
    setMessages(initialMessages);
    setIsStreaming(false);
    setStreamingMsgId(null);
    setLlmCalling(null);
    setStreamJustFinished(null);
    setPendingConfirmation(null);
    setSlotInputs({});
    setStepSelections({});
    setRetryArgsText({});
    setIsTyping(false);
  }, [cancelStream, initialMessages]);

  return {
    messages,
    setMessages,
    isStreaming,
    setIsStreaming,
    streamingMsgId,
    setStreamingMsgId,
    llmCalling,
    setLlmCalling,
    streamJustFinished,
    setStreamJustFinished,
    lastStepReached,
    setLastStepReached,
    pendingConfirmation,
    setPendingConfirmation,
    slotInputs,
    setSlotInputs,
    stepSelections,
    setStepSelections,
    retryArgsText,
    setRetryArgsText,
    isTyping,
    setIsTyping,
    sendMessage,
    confirmAction,
    retryAction,
    skipAction,
    cancelStream,
    reset,
  };
}
